// Hook function.  Defaults to an empty stub function
let consoleHook = (_s) => { };
// Internal util method to print any interesting data/comms to the console hook
function printToHook(s) {
    try {
        consoleHook(s);
    }
    catch (e) {
        console.error('Error in setConsoleHook:', e);
    }
}
/** @hidden */
function setConsoleHook(hookFunc) {
    // send a quick output line and trap. That way if hook is broken we won't install a broken handler
    try {
        hookFunc('Hook Installed');
        consoleHook = hookFunc;
    }
    catch (e) {
        console.error('Error in setConsoleHook:', e);
    }
}

class CommandFailedError extends Error {
    constructor(message) {
        super(message);
        this.name = 'CommandFailedError';
    }
}

class CommandNotAvailableError extends Error {
    constructor(message) {
        super(message);
        this.name = 'CommandNotAvailableError';
    }
}

class DispatchFailedError extends Error {
    constructor(message) {
        super(message);
        this.name = 'DispatchFailedError';
    }
}

var Errors = { CommandFailedError, CommandNotAvailableError, DispatchFailedError };

var _a;
const globalWithCounter = globalThis;
(_a = globalWithCounter.__INDEED_JSMA_BRIDGE_INTERNAL_ID_COUNTER) !== null && _a !== void 0 ? _a : (globalWithCounter.__INDEED_JSMA_BRIDGE_INTERNAL_ID_COUNTER = 0);
globalWithCounter.__INDEED_JSMA_BRIDGE_INTERNAL_ID_COUNTER++;
const bridgeInstanceId = globalWithCounter.__INDEED_JSMA_BRIDGE_INTERNAL_ID_COUNTER.toString();
// Incrementing unique id used as key to store response callbacks
let uid = 1000;
// Map of response callbacks
let responseCallbacks = {};
// Available method map
let availableMethodsMap = null;
/**
 * Sends string to the native app console log where it can be viewed in the native app
 *  console log viewer. All messages sent here will *always* be printed to window.console.log
 *  as well.
 * All failures are suppressed.
 */
function appConsoleLog(s) {
    try {
        const commandJson = makeCallbackCommand('appConsoleLog', { logStr: s }, null);
        dispatchCommand(commandJson);
    }
    catch (e) {
        // Swallow do nothing
    }
    window.console.log(s);
}
const getErrorMessage = (e) => {
    return typeof e === 'string' ? e : e instanceof Error ? e.message : 'Unknown error';
};
// Messages come in as a json string. See the type "WrappedAppResult"
function delegateAppResult(json) {
    var _a;
    printToHook(`Delegating: ${json}`);
    try {
        const resultWrapper = JSON.parse(json);
        const result = resultWrapper.resultObj;
        const callbackId = result.callbackId;
        if (!callbackId) {
            // If no callback, do nothing
            return;
        }
        const match = callbackId.match(/__instanceid__(\S+)$/);
        const instanceId = match && match[1];
        if (!instanceId) {
            printToHook(`Instance ID missing ${callbackId} - skipping`);
            return;
        }
        const handler = (_a = window.JSMABridgeInstances) === null || _a === void 0 ? void 0 : _a[instanceId];
        if (!handler) {
            printToHook(`Bridge receiver for ID missing ${callbackId} - skipping`);
            return;
        }
        // Forward the payload to the appropriate instance
        handler.receiveAppResult(json);
    }
    catch (e) {
        appConsoleLog(`BridgeHelper Error: ${getErrorMessage(e)}`);
    }
}
// Messages come in as a json string. See the type "WrappedAppResult"
function receiveAppResult(json) {
    printToHook(`Received: ${json}`);
    try {
        const resultWrapper = JSON.parse(json);
        const result = resultWrapper.resultObj;
        if (!result.callbackId) {
            // If no callback, do nothing
            return;
        }
        const callbackMethod = responseCallbacks[result.callbackId];
        if (!callbackMethod) {
            // This can happen if native calls receiveAppResult multiple times
            // For example, if an interaction is not properly debounced
            return;
        }
        if (result.error) {
            let errorInstance;
            if (typeof result.error === 'string') {
                // Assume result.error is the message for legacy handling
                errorInstance = new Errors.CommandFailedError(result.error);
            }
            else {
                const { errorType, message } = result.error;
                const ErrorClass = isKnownErrorType(errorType)
                    ? Errors[errorType]
                    : Errors.CommandFailedError;
                errorInstance = new ErrorClass(message);
            }
            callbackMethod({ success: false, err: errorInstance });
        }
        else {
            callbackMethod({
                success: true,
                data: result.result,
            });
        }
        // remove the callback from dict
        Reflect.deleteProperty(responseCallbacks, result.callbackId);
    }
    catch (e) {
        appConsoleLog(`BridgeHelper Error: ${getErrorMessage(e)}`);
    }
}
function isKnownErrorType(key) {
    return key in Errors;
}
// Creates a "command" json envelope on which to serialize requests to underlying native bridge.
function makeCallbackCommand(command, data, callbackId) {
    return {
        command,
        callbackId,
        data: data || {},
    };
}
// Underlying helper method that marshalls the command to the native interface
function sendCommandToNative(commandName, inputData, callback) {
    let callbackId;
    try {
        // if we have a callback then store it in our response callbacks
        if (callback) {
            callbackId = `__callbackid__${uid.toString()}__instanceid__${bridgeInstanceId}`;
            responseCallbacks[callbackId] = callback;
            uid += 1;
        }
        // Make a command envelope
        const command = makeCallbackCommand(commandName, inputData, callbackId || null);
        // Print to console hook if exists
        printToHook(`Dispatch: ${JSON.stringify(command)}`);
        // Dispatch
        dispatchCommand(command);
    }
    catch (e) {
        const errorMessage = getErrorMessage(e);
        if (callbackId) {
            const callbackMethod = responseCallbacks[callbackId];
            Reflect.deleteProperty(responseCallbacks, callbackId);
            if (callbackMethod) {
                callbackMethod({ success: true, data: errorMessage });
            }
        }
        else {
            appConsoleLog(`BridgeHelper Error: ${errorMessage}`);
        }
    }
}
/**
 * Dispatch the command to native. Internally detects and sends the command to either the Android or iOS platform.
 */
function dispatchCommand(command) {
    const androidInterface = getAndroidNativeInterface();
    const iosInterface = getIosNativeInterface();
    if (androidInterface) {
        androidInterface.dispatchCommand(JSON.stringify(command));
        return;
    }
    else if (iosInterface) {
        iosInterface.postMessage(command);
        return;
    }
    throw new Errors.DispatchFailedError('Could not find compatible native interface');
}
function getAndroidNativeInterface() {
    return window.jsmNativeInterfaceV2;
}
function getIosNativeInterface() {
    return (window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.JSMWebKitBridgeDispatchV2);
}
function isMethodAvailable(commandName, callback) {
    const lookUpInCachedMapAndCallback = () => {
        if (availableMethodsMap && availableMethodsMap[commandName]) {
            callback({
                success: true,
                data: { commandName, isMethodAvailable: true },
            });
        }
        else {
            callback({
                success: false,
                err: new Errors.CommandNotAvailableError(`method ${commandName} not in available command map`),
            });
        }
    };
    if (availableMethodsMap) {
        lookUpInCachedMapAndCallback();
        return;
    }
    sendCommandToNative('getAvailableMethods', {}, (res) => {
        if (!res.success) {
            callback(res);
            return;
        }
        // Map availableMethods to a map that looks like { MethodName: true, ... }
        availableMethodsMap = {};
        const availableMethodsList = res.data.availableMethods;
        for (const availableMethod of availableMethodsList) {
            availableMethodsMap[availableMethod] = true;
        }
        lookUpInCachedMapAndCallback();
    });
}
/**
 * Allow list of synthetic events
 */
const ALL_SYNTHETIC_EVENTS = ['nativeWebViewAppear'];
/**
 * Dispatches synthetic events to the window
 */
function dispatchWindowEvent(eventJSON) {
    printToHook(`Dispatching synthetic window event: ${eventJSON}`);
    try {
        // parse the event envelope
        const synthEventEnvelope = JSON.parse(eventJSON);
        const eventName = synthEventEnvelope.event;
        // Ensure that eventName is valid in our SyntheticEvent enums
        if (!ALL_SYNTHETIC_EVENTS.includes(eventName)) {
            appConsoleLog(`Invalid synthetic eventName: ${eventName}`);
            return;
        }
        // build event and dispatch
        const customEvent = new CustomEvent(eventName, {});
        window.dispatchEvent(customEvent);
        appConsoleLog(`Synthetic event dispatched: ${eventName}`);
    }
    catch (e) {
        appConsoleLog(`BridgeHelper Error in dispatchWindowEvent: ${getErrorMessage(e)}`);
    }
}

/**
 * An enumeration corresponding to icons in the Indeed design library.
 * Mirrors icons available in v4.20.1-alpha.10
 * @enum string
 * {@link https://www.figma.com/file/rki5osPNi2vy01TG7ZKhZO/Indeed-Design-Library?node-id=3173%3A47812|Figma file}
 * {@link https://code.corp.indeed.com/indeed-design-system/ifl/-/blob/develop/packages/icons/src/index.ts|Icon package definition}
 */
var Icon;
(function (Icon) {
    Icon["ADDITIONAL_INFO"] = "ADDITIONAL_INFO";
    Icon["ADD_PERSON"] = "ADD_PERSON";
    Icon["ADD_TO_FOLDER"] = "ADD_TO_FOLDER";
    Icon["ANDROID_LOGO"] = "ANDROID_LOGO";
    Icon["APPLE_LOGO"] = "APPLE_LOGO";
    Icon["ARCHIVE"] = "ARCHIVE";
    Icon["ARROW_DOWN"] = "ARROW_DOWN";
    Icon["ARROW_LEFT"] = "ARROW_LEFT";
    Icon["ARROW_RIGHT"] = "ARROW_RIGHT";
    Icon["ARROW_UP"] = "ARROW_UP";
    Icon["ATTACHMENT"] = "ATTACHMENT";
    Icon["AWARD"] = "AWARD";
    Icon["BAN"] = "BAN";
    Icon["BIKE"] = "BIKE";
    Icon["BOOKMARK"] = "BOOKMARK";
    Icon["BOOKMARK_OUTLINE"] = "BOOKMARK_OUTLINE";
    Icon["BULLETED_LIST"] = "BULLETED_LIST";
    Icon["CALENDAR"] = "CALENDAR";
    Icon["CALENDAR_GREEN"] = "CALENDAR_GREEN";
    Icon["CAMERA"] = "CAMERA";
    Icon["CAMPAIGN"] = "CAMPAIGN";
    Icon["CAR"] = "CAR";
    Icon["CARET_DOWN"] = "CARET_DOWN";
    Icon["CARET_DUAL"] = "CARET_DUAL";
    Icon["CARET_UP"] = "CARET_UP";
    Icon["CERTIFICATION"] = "CERTIFICATION";
    Icon["CHAT"] = "CHAT";
    Icon["CHECK"] = "CHECK";
    Icon["CHECK_CIRCLE"] = "CHECK_CIRCLE";
    Icon["CHECK_CIRCLE_BLUE"] = "CHECK_CIRCLE_BLUE";
    Icon["CHEVRON_DOWN"] = "CHEVRON_DOWN";
    Icon["CHEVRON_LEFT"] = "CHEVRON_LEFT";
    Icon["CHEVRON_RIGHT"] = "CHEVRON_RIGHT";
    Icon["CHEVRON_UP"] = "CHEVRON_UP";
    Icon["CLICK"] = "CLICK";
    Icon["CLIPBOARD"] = "CLIPBOARD";
    Icon["CLOCK"] = "CLOCK";
    Icon["CLOCK_FILLED"] = "CLOCK_FILLED";
    Icon["CLOSE"] = "CLOSE";
    Icon["CLOSE_CIRCLE"] = "CLOSE_CIRCLE";
    Icon["CLOSE_CIRCLE_RED"] = "CLOSE_CIRCLE_RED";
    Icon["COLLAPSE"] = "COLLAPSE";
    Icon["COMMUTE"] = "COMMUTE";
    Icon["COMPANY"] = "COMPANY";
    Icon["COMPARISON"] = "COMPARISON";
    Icon["CONVERT_TO_EDITABLE"] = "CONVERT_TO_EDITABLE";
    Icon["COPY"] = "COPY";
    Icon["CREDIT_CARD"] = "CREDIT_CARD";
    Icon["CURRENT_LOCATION"] = "CURRENT_LOCATION";
    Icon["DATA_BAR"] = "DATA_BAR";
    Icon["DATA_LINE_DOWN"] = "DATA_LINE_DOWN";
    Icon["DATA_LINE_FLAT"] = "DATA_LINE_FLAT";
    Icon["DATA_LINE_UP"] = "DATA_LINE_UP";
    Icon["DATE"] = "DATE";
    Icon["DATE_APPROVED"] = "DATE_APPROVED";
    Icon["DATE_CANCELED"] = "DATE_CANCELED";
    Icon["DATE_SCHEDULED"] = "DATE_SCHEDULED";
    Icon["DESKTOP"] = "DESKTOP";
    Icon["DESKTOP_REPORT"] = "DESKTOP_REPORT";
    Icon["DISCOVER"] = "DISCOVER";
    Icon["DOC_REVIEW"] = "DOC_REVIEW";
    Icon["DOUBLE_CHEVRON_LEFT"] = "DOUBLE_CHEVRON_LEFT";
    Icon["DOUBLE_CHEVRON_RIGHT"] = "DOUBLE_CHEVRON_RIGHT";
    Icon["DOWNLOAD"] = "DOWNLOAD";
    Icon["EDIT"] = "EDIT";
    Icon["EDIT_SECTION"] = "EDIT_SECTION";
    Icon["EFFECTS"] = "EFFECTS";
    Icon["ERROR"] = "ERROR";
    Icon["EXTERNAL_LINK"] = "EXTERNAL_LINK";
    Icon["EXPAND"] = "EXPAND";
    Icon["FACEBOOK_LOGO"] = "FACEBOOK_LOGO";
    Icon["FILE_DOC"] = "FILE_DOC";
    Icon["FILE_IMAGE"] = "FILE_IMAGE";
    Icon["FILE_MISC"] = "FILE_MISC";
    Icon["FILTER"] = "FILTER";
    Icon["FOLDER"] = "FOLDER";
    Icon["GET_DIRECTIONS"] = "GET_DIRECTIONS";
    Icon["GUARANTEE"] = "GUARANTEE";
    Icon["HAMBURGER"] = "HAMBURGER";
    Icon["HEART"] = "HEART";
    Icon["HEART_OUTLINE"] = "HEART_OUTLINE";
    Icon["HIDE"] = "HIDE";
    Icon["HIRING_BADGE"] = "HIRING_BADGE";
    Icon["HOME"] = "HOME";
    Icon["HOME_INTERVIEW"] = "HOME_INTERVIEW";
    Icon["HOURGLASS"] = "HOURGLASS";
    Icon["INFORMATION"] = "INFORMATION";
    Icon["INSERT_TEXT_TEMPLATE"] = "INSERT_TEXT_TEMPLATE";
    Icon["INSERT_VARIABLE"] = "INSERT_VARIABLE";
    Icon["INSTAGRAM_LOGO"] = "INSTAGRAM_LOGO";
    Icon["INTERVIEW"] = "INTERVIEW";
    Icon["JOBS"] = "JOBS";
    Icon["JOB_PREFERENCE"] = "JOB_PREFERENCE";
    Icon["JOB_TAG"] = "JOB_TAG";
    Icon["LANGUAGE"] = "LANGUAGE";
    Icon["LINK"] = "LINK";
    Icon["LINKEDIN_LOGO"] = "LINKEDIN_LOGO";
    Icon["LOCATION"] = "LOCATION";
    Icon["LOCK"] = "LOCK";
    Icon["MAIL"] = "MAIL";
    Icon["MAIL_OPEN"] = "MAIL_OPEN";
    Icon["MAXIMIZE"] = "MAXIMIZE";
    Icon["MIC"] = "MIC";
    Icon["MIC_MUTED"] = "MIC_MUTED";
    Icon["MINIMIZE"] = "MINIMIZE";
    Icon["MINUS"] = "MINUS";
    Icon["MINUS_CIRCLE"] = "MINUS_CIRCLE";
    Icon["MOBILE"] = "MOBILE";
    Icon["MONEY_STACK"] = "MONEY_STACK";
    Icon["MORE_HORIZONTAL"] = "MORE_HORIZONTAL";
    Icon["MORE_VERTICAL"] = "MORE_VERTICAL";
    Icon["MOVE_BACK"] = "MOVE_BACK";
    Icon["MOVE_SECTION"] = "MOVE_SECTION";
    Icon["MOVE_TO_FOLDER"] = "MOVE_TO_FOLDER";
    Icon["MY_CONTRIBUTIONS"] = "MY_CONTRIBUTIONS";
    Icon["MY_PAGE"] = "MY_PAGE";
    Icon["NATIVE_IOS_CHEVRON_BACK"] = "NATIVE_IOS_CHEVRON_BACK";
    Icon["NATIVE_IOS_CHEVRON_FORWARD"] = "NATIVE_IOS_CHEVRON_FORWARD";
    Icon["NATIVE_IOS_MORE"] = "NATIVE_IOS_MORE";
    Icon["NATIVE_IOS_SAFARI"] = "NATIVE_IOS_SAFARI";
    Icon["NATIVE_IOS_SHARE"] = "NATIVE_IOS_SHARE";
    Icon["NATIVE_IOS_TEXT_FORMAT"] = "NATIVE_IOS_TEXT_FORMAT";
    Icon["NATIVE_ANDROID_ARROW_BACK"] = "NATIVE_ANDROID_ARROW_BACK";
    Icon["NATIVE_ANDROID_ARROW_FORWARD"] = "NATIVE_ANDROID_ARROW_FORWARD";
    Icon["NATIVE_ANDROID_MORE"] = "NATIVE_ANDROID_MORE";
    Icon["NATIVE_ANDROID_SHARE"] = "NATIVE_ANDROID_SHARE";
    Icon["NEW_WINDOW"] = "NEW_WINDOW";
    Icon["NEW_WINDOW_RTL"] = "NEW_WINDOW_RTL";
    Icon["NOTES"] = "NOTES";
    Icon["NOTIFICATION"] = "NOTIFICATION";
    Icon["NUMBERED_LIST"] = "NUMBERED_LIST";
    Icon["OCCUPATION"] = "OCCUPATION";
    Icon["OPEN_DOOR"] = "OPEN_DOOR";
    Icon["PATENT"] = "PATENT";
    Icon["PAUSE"] = "PAUSE";
    Icon["PEOPLE"] = "PEOPLE";
    Icon["PERSON_SEARCH"] = "PERSON_SEARCH";
    Icon["PHONE"] = "PHONE";
    Icon["PLACEHOLDER"] = "PLACEHOLDER";
    Icon["PLANE"] = "PLANE";
    Icon["PLAY"] = "PLAY";
    Icon["PLUS"] = "PLUS";
    Icon["PLUS_CIRCLE"] = "PLUS_CIRCLE";
    Icon["POST_JOB"] = "POST_JOB";
    Icon["PRINT"] = "PRINT";
    Icon["PROFILE"] = "PROFILE";
    Icon["PROFILE_CIRCLE"] = "PROFILE_CIRCLE";
    Icon["PROFILE_GREEN"] = "PROFILE_GREEN";
    Icon["PROMOTIONAL_OPPORTUNITY"] = "PROMOTIONAL_OPPORTUNITY";
    Icon["PUBLICATION"] = "PUBLICATION";
    Icon["QUESTION"] = "QUESTION";
    Icon["QUESTION_CIRCLE"] = "QUESTION_CIRCLE";
    Icon["QUICK_APPLY"] = "QUICK_APPLY";
    Icon["QUICK_INTERVIEW"] = "QUICK_INTERVIEW";
    Icon["REARRANGE_ITEMS"] = "REARRANGE_ITEMS";
    Icon["RECENT_SEARCH"] = "RECENT_SEARCH";
    Icon["REFRESH"] = "REFRESH";
    Icon["RENEW"] = "RENEW";
    Icon["REPLACE_FILE"] = "REPLACE_FILE";
    Icon["REPLY"] = "REPLY";
    Icon["REPLY_ALL"] = "REPLY_ALL";
    Icon["REPORT"] = "REPORT";
    Icon["RESET"] = "RESET";
    Icon["RESPONSIVE"] = "RESPONSIVE";
    Icon["RESTORE"] = "RESTORE";
    Icon["RESUME"] = "RESUME";
    Icon["SALARY"] = "SALARY";
    Icon["SALARY_YEN"] = "SALARY_YEN";
    Icon["SAVE_SEARCH"] = "SAVE_SEARCH";
    Icon["SEARCH"] = "SEARCH";
    Icon["SEARCH_SAVED"] = "SEARCH_SAVED";
    Icon["SETTINGS"] = "SETTINGS";
    Icon["SHARE"] = "SHARE";
    Icon["SHOW"] = "SHOW";
    Icon["SIGN_OUT"] = "SIGN_OUT";
    Icon["SORT"] = "SORT";
    Icon["SORT_ASCENDING"] = "SORT_ASCENDING";
    Icon["SORT_DESCENDING"] = "SORT_DESCENDING";
    Icon["SPAM"] = "SPAM";
    Icon["SPEAKERS"] = "SPEAKERS";
    Icon["SPEAKERS_MUTED"] = "SPEAKERS_MUTED";
    Icon["STAR"] = "STAR";
    Icon["STAR_HALF"] = "STAR_HALF";
    Icon["STAR_OUTLINE"] = "STAR_OUTLINE";
    Icon["STOP"] = "STOP";
    Icon["STOPWATCH"] = "STOPWATCH";
    Icon["STORYBOOK"] = "STORYBOOK";
    Icon["SUMMARY"] = "SUMMARY";
    Icon["SUPER_POPULAR"] = "SUPER_POPULAR";
    Icon["THUMBS_DOWN"] = "THUMBS_DOWN";
    Icon["THUMBS_DOWN_RED"] = "THUMBS_DOWN_RED";
    Icon["THUMBS_UP"] = "THUMBS_UP";
    Icon["THUMBS_UP_GREEN"] = "THUMBS_UP_GREEN";
    Icon["TRAIN"] = "TRAIN";
    Icon["TRASH_CAN"] = "TRASH_CAN";
    Icon["TWITTER_LOGO"] = "TWITTER_LOGO";
    Icon["UNLOCK"] = "UNLOCK";
    Icon["UPLOAD"] = "UPLOAD";
    Icon["VIDEO"] = "VIDEO";
    Icon["WAFFLE"] = "WAFFLE";
    Icon["WALKING"] = "WALKING";
    Icon["WARNING"] = "WARNING";
    Icon["WEB_PAGE"] = "WEB_PAGE";
    Icon["YOUTUBE_LOGO"] = "YOUTUBE_LOGO";
    // Non IDL app specific icons
    // https://www.figma.com/file/q6OytUS1hQAJPW39XK9dPp/JSMA-%2F-Material-Design-Icons?node-id=644%3A42
    Icon["OPEN_IN_BROWSER"] = "OPEN_IN_BROWSER";
    Icon["FONT_SIZE"] = "FONT_SIZE";
})(Icon || (Icon = {}));

// Shared types go here
/**
 * @enum {string}
 * @property {string} LIGHT - light theme
 * @property {string} DARK - dark theme
 */
var ToastTheme;
(function (ToastTheme) {
    ToastTheme["LIGHT"] = "light";
    ToastTheme["DARK"] = "dark";
})(ToastTheme || (ToastTheme = {}));
var ToastStyle;
(function (ToastStyle) {
    ToastStyle["INFO"] = "info";
    ToastStyle["WARNING"] = "warning";
    ToastStyle["ERROR"] = "error";
    ToastStyle["GENERIC"] = "generic";
})(ToastStyle || (ToastStyle = {}));
/**
 * @enum {string}
 * @property {string} TOP - Toast displayed in the top of the app's screen
 * @property {string} CENTER - Toast displayed in the center/middle of the app's screen
 * @property {string} BOTTOM - Toast displayed in the bottom of the app's screen
 */
var ToastPosition;
(function (ToastPosition) {
    ToastPosition["TOP"] = "top";
    ToastPosition["CENTER"] = "center";
    ToastPosition["BOTTOM"] = "bottom";
})(ToastPosition || (ToastPosition = {}));
/**
 * @enum {string} This is used in the `sendPageInfo()` function to indicate the page type.
 * @property {string} ViewJob - A /viewjob page.
 */
var PageType;
(function (PageType) {
    PageType["ViewJob"] = "ViewJob";
})(PageType || (PageType = {}));
/**
 * @enum {string} Used to store the
 * @property {string} WHAT - Content search based on job title, keyword, or company
 * @property {string} WHERE - Location search based on city, state, zip code, or "remote"
 */
var SearchType;
(function (SearchType) {
    SearchType["WHAT"] = "WHAT";
    SearchType["WHERE"] = "WHERE";
})(SearchType || (SearchType = {}));

/**
 * Sets the app badge count to the given value.
 *
 * @platform iOS
 */
function setAppBadgeCount(params) {
    return sendOneWayCommand('setAppBadgeCount', params);
}
/**
 * Clears the current badge count
 *
 * @platform iOS
 */
function clearAppBadgeCount() {
    return sendOneWayCommand('setAppBadgeCount', { badgeCount: 0 });
}
/**
 * Share natively a content from the app.
 *
 * @platform iOS, Android
 */
function shareContent(params) {
    var _a;
    return sendOneWayCommand('shareContent', {
        url: params.url,
        title: (_a = params.title) !== null && _a !== void 0 ? _a : null,
    });
}
/**
 * Opens a modal screen with webview
 *
 * @platform iOS, Android
 */
async function showModalWithWebView(params) {
    var _a, _b, _c;
    // TODO: Remove <null> fallback for params after confimation on native-side compatibility
    return sendCommand('showModalWithWebView', {
        url: params.url,
        title: (_a = params.title) !== null && _a !== void 0 ? _a : null,
        presentationMode: (_b = params.presentationMode) !== null && _b !== void 0 ? _b : null,
        height: (_c = params.height) !== null && _c !== void 0 ? _c : null,
    });
}
/**
 * Opens a modal screen with webview and wait for result when modal is dimissed
 *
 * @platform iOS, Android
 */
async function showModalWithWebViewAndWaitForResult(params) {
    return sendCommand('showModalWithWebView', params);
}
/**
 * Closes the modal programmatically
 *
 * @platform iOS, Android
 */
async function dismissModalWithWebView(params) {
    return sendCommand('dismissModalWithWebView', params);
}
/**
 * Opens a dialog in the app to prompt the user to send an error or usage report with diagnostics info.
 * The contents of the report is determined by the application.
 *
 * @platform Android
 */
async function openShareDiagnosticsInfoDialog(params) {
    var _a;
    return sendOneWayCommand('openShareDiagnosticsInfoDialog', { from: (_a = params.from) !== null && _a !== void 0 ? _a : null });
}
/**
 * Opens an action overflow menu with the requested items and heading.
 * This is an asynchronous method and will return the result of the user selection or cancellation
 * in the result when the menu is dimissed.
 *
 * @platform iOS, Android
 * @returns Promise resolves on menu close to return result of selection, rejects if the menu cannot be opened due to invalid arguments (iOS only)
 */
async function showActionOverflowMenu(params) {
    var _a, _b, _c, _d;
    return sendCommand('showActionOverflowMenu', {
        items: params.items,
        heading: (_a = params.heading) !== null && _a !== void 0 ? _a : null,
        subheading: (_b = params.subheading) !== null && _b !== void 0 ? _b : null,
        subheadingIcon: (_c = params.subheadingIcon) !== null && _c !== void 0 ? _c : null,
        currentSelectedIndex: (_d = params.currentSelectedIndex) !== null && _d !== void 0 ? _d : null,
    });
}
/**
 * Returns version and platform information about the native host app if any.
 *
 * @platform iOS, Android
 * @returns Returns nil result and error if method not available or is code is not hosted in JSMA.
 *          Returns appSessionId and sessionId for app version greater than or equal to v207.0 (iOS) and v179.0 (Android)
 */
async function getNativeAppInfo() {
    return sendCommand('getNativeAppInfo', {});
}
/**
 * Initiate a phone call without going through the Dialer user interface for the user to confirm the call.
 * If needed, user's permission will be asked first. This method is only operable on Android.
 *
 * @platform Android
 * @returns returns CallPhoneNumberResult if successful.
 */
async function callPhoneNumber(params) {
    return sendCommand('callPhoneNumber', params);
}
/**
 * Notifies app that indeed apply completed and should return to page that opened viewjob.
 * If postApplyUrl is non-null then the postApplyUrl should be opened as a forward navigation from the page that opened viewjob.
 * If the postApplyConfirmMessage is non-null then a confirmation toast should appear that displays the postApplyMessage string.  If the confirmation toast is shown, then the postApplyConfirmUrl will indicate (if present) - the link that should be navigated to on user tap.
 * The postApplyConfirmMessage should support simple HTML span based styling, including: font color, underline, bold, italic.
 *
 * @platform iOS, Android
 */
async function indeedApplyDidComplete(params) {
    var _a, _b, _c;
    return sendCommand('indeedApplyDidComplete', {
        postApplyUrl: (_a = params.postApplyUrl) !== null && _a !== void 0 ? _a : null,
        postApplyConfirmMessage: (_b = params.postApplyConfirmMessage) !== null && _b !== void 0 ? _b : null,
        postApplyConfirmUrl: (_c = params.postApplyConfirmUrl) !== null && _c !== void 0 ? _c : null,
    });
}
/**
 * Initiates an Indeed Apply - potentially in a new webview screen/layer as a modal process.
 *  Note: An Indeed Apply started by this function will not implement applyDidComplete behavior on completion.
 *
 * @platform iOS, Android
 * @returns Promise resolves when apply is complete.
 */
async function startIndeedApply(params) {
    return sendCommand('startIndeedApply', params);
}
/**
 * Notifies app to open programUrl in a new webview and to call jsScriptUrl to retrieve the JS script that will be injected in that webview.
 *
 * @platform Android
 */
async function openEducationPage(params) {
    return sendCommand('openEducationPage', params);
}
/**
 * Notifies app to display a toast with the given texts and specified duration/theme/style/position.
 *
 * @platform iOS
 */
async function displayToast(params) {
    var _a, _b, _c;
    return sendCommand('displayToast', {
        message: params.message,
        durationSeconds: params.durationSeconds,
        title: (_a = params.title) !== null && _a !== void 0 ? _a : null,
        icon: (_b = params.icon) !== null && _b !== void 0 ? _b : null,
        theme: (_c = params.theme) !== null && _c !== void 0 ? _c : null,
        style: params.style || ToastStyle.GENERIC,
        position: params.position || ToastPosition.BOTTOM,
    });
}
/**
 * Ask the app to show an error alert.
 *
 * @platform iOS
 */
async function displayErrorAlert(params) {
    var _a;
    return sendCommand('displayErrorAlert', {
        message: params.message,
        title: (_a = params.title) !== null && _a !== void 0 ? _a : null,
    });
}
/**
 * @platform iOS, Android
 */
async function sendPageInfo(params) {
    return sendCommand('sendPageInfo', params);
}
/**
 * Tells the app what current step and total steps are there in VIP onboarding
 *
 * @platform iOS, Android
 */
function sendVIPStepInfo(params) {
    return sendCommand('sendVIPStepInfo', params);
}
/**
 * Tells the app to open the Push Notification Preferences Settings panel
 *
 * @platform iOS, Android
 */
async function showOpenPushPrefsPanel() {
    return sendCommand('showOpenPushPrefsPanel', {});
}
/**
 * Opens a native search overlay for "What" or "Where" search
 *
 * @platform iOS, Android
 * @returns Promise resolves when native search overlay is rendered, or rejects if an error occurs.
 */
async function showSearchOverlay(params) {
    var _a, _b, _c;
    return sendCommand('showSearchOverlay', {
        searchType: params.searchType,
        whatInput: (_a = params.whatInput) !== null && _a !== void 0 ? _a : null,
        whereInput: (_b = params.whereInput) !== null && _b !== void 0 ? _b : null,
        origin: (_c = params.origin) !== null && _c !== void 0 ? _c : null,
    });
}
/**
 * Check the state of the native geolocation permission.
 *
 * @platform Android
 */
async function isGeolocationGranted() {
    return sendCommand('isGeolocationGranted', {});
}
/**
 * Opens app info page on Android.
 *
 * @platform Android
 */
async function openAndroidAppInfo() {
    return sendCommand('openAndroidAppInfo', {});
}
/**
 * Register click handler for the save button on native navigation bar
 *
 * @platform iOS, Android
 */
async function registerViewJobNavBarSaveButtonClickHandler(params) {
    if (window.JSMABridgeRegistry) {
        window.JSMABridgeRegistry[params.handlerIdentifier] = params.handler;
    }
    return sendCommand('registerViewJobNavBarSaveButtonClickHandler', {
        handlerIdentifier: params.handlerIdentifier,
    });
}
/**
 * Set job save states for the save button on native navigation bar
 *
 * @platform iOS, Android
 */
async function setViewJobNavBarSaveButtonState(params) {
    return sendCommand('setViewJobNavBarSaveButtonState', params);
}
/**
 * Navigate to profile home page
 *
 * @platform iOS
 * @retruns Promise resolves when navigation is finished, or rejects when error occurs.
 */
async function navigateToProfileHomePage() {
    return sendCommand('navigateToProfileHomePage', {});
}
/**
 * Navigate to login modal
 *
 * @platform iOS
 */
async function navigateToLogin(params) {
    return sendCommand('navigateToLogin', params);
}
/**
 * Ask the app to set custom heading title text
 *
 * @platform iOS, Android
 */
async function setNavigationTitle(params) {
    return sendCommand('setNavigationTitle', params);
}
/**
 * Opens a modal screen to show the paywall.
 *
 * @platform iOS
 */
async function displayPaywall(params) {
    return sendCommand('displayPaywall', params);
}
/**
 * Opens a modal screen to show the subscription management.
 *
 * @platform iOS
 */
async function displaySubscriptionManagement() {
    return sendCommand('displaySubscriptionManagement', {});
}
/**
 * Dismiss the apply WebView
 *
 * @platform iOS, Android
 */
async function dismissApplyWebView() {
    return sendOneWayCommand('dismissApplyWebView', {});
}
/**
 * Notify app onboarding that the user has completed all web onboarding questions
 *
 * @platform iOS, Android
 */
async function webOnboardingOnComplete(params) {
    return sendOneWayCommand('webOnboardingOnComplete', params);
}
/**
 * Display the JS Premium/Pro WVMP (Who viewed my profile, aka. profile insights) details screen
 *
 * @platform iOS, Android
 */
async function displayWVMPDetails() {
    return sendCommand('displayWVMPDetails', {});
}
/**
 * Check whether the bridge is available
 *
 * @platform iOS, Android
 */
function isAvailable() {
    return !!(getAndroidNativeInterface() || getIosNativeInterface());
}
//////////////////////////////////////////////////////
// Execute registered callback
//////////////////////////////////////////////////////
/**
 * Check whether a callback is registered and execute if found
 *
 * @platform iOS, Android
 * @hidden
 */
function executeCallback(handlerIdentifier) {
    const handler = window.JSMABridgeRegistry && window.JSMABridgeRegistry[handlerIdentifier];
    if (!handler) {
        appConsoleLog(`Handler not found for identifier: ${handlerIdentifier}`);
        return;
    }
    try {
        handler();
    }
    catch (e) {
        appConsoleLog(`Handler ${handlerIdentifier} error: ${getErrorMessage(e)}`);
    }
}
//////////////////////////////////////////////////////
// Utilities
//////////////////////////////////////////////////////
// Utility function to check if bridge method is avaialble, throw err when not
async function throwIfMethodNotAvailable(commandName) {
    return new Promise((resolve, reject) => {
        isMethodAvailable(commandName, (res) => {
            if (!res.success) {
                reject(res.err);
                return;
            }
            if (!res.data.isMethodAvailable) {
                reject(new CommandNotAvailableError('Command not available'));
                return;
            }
            resolve();
        });
    });
}
// Send one-way command, doesn't wait for the callback from native side
async function sendOneWayCommand(commandName, inputData) {
    await throwIfMethodNotAvailable(commandName);
    sendCommandToNative(commandName, inputData);
}
// Send two-way command, wait for the callback from native side
async function sendCommand(commandName, inputData) {
    await throwIfMethodNotAvailable(commandName);
    return new Promise((resolve, reject) => {
        sendCommandToNative(commandName, inputData, (res) => {
            if (!res.success) {
                reject(res.err);
                return;
            }
            resolve(res.data);
        });
    });
}
// These are exposed for debug usage only. Client apps should not need these.
/** @hidden */
const _INTERNAL_ = {
    appConsoleLog,
    sendCommandToNative,
    bridgeTest: (str) => {
        // signal to native
        sendCommandToNative('bridgeTest', { str });
    },
    bridgeTestAsync: (str, callback) => {
        sendCommandToNative('bridgeTestAsync', { str }, callback);
    },
};

var JSMABridge = /*#__PURE__*/Object.freeze({
    __proto__: null,
    get Icon () { return Icon; },
    _INTERNAL_: _INTERNAL_,
    callPhoneNumber: callPhoneNumber,
    clearAppBadgeCount: clearAppBadgeCount,
    dismissApplyWebView: dismissApplyWebView,
    dismissModalWithWebView: dismissModalWithWebView,
    displayErrorAlert: displayErrorAlert,
    displayPaywall: displayPaywall,
    displaySubscriptionManagement: displaySubscriptionManagement,
    displayToast: displayToast,
    displayWVMPDetails: displayWVMPDetails,
    executeCallback: executeCallback,
    getNativeAppInfo: getNativeAppInfo,
    indeedApplyDidComplete: indeedApplyDidComplete,
    isAvailable: isAvailable,
    isGeolocationGranted: isGeolocationGranted,
    navigateToLogin: navigateToLogin,
    navigateToProfileHomePage: navigateToProfileHomePage,
    openAndroidAppInfo: openAndroidAppInfo,
    openEducationPage: openEducationPage,
    openShareDiagnosticsInfoDialog: openShareDiagnosticsInfoDialog,
    registerViewJobNavBarSaveButtonClickHandler: registerViewJobNavBarSaveButtonClickHandler,
    sendPageInfo: sendPageInfo,
    sendVIPStepInfo: sendVIPStepInfo,
    setAppBadgeCount: setAppBadgeCount,
    setConsoleHook: setConsoleHook,
    setNavigationTitle: setNavigationTitle,
    setViewJobNavBarSaveButtonState: setViewJobNavBarSaveButtonState,
    shareContent: shareContent,
    showActionOverflowMenu: showActionOverflowMenu,
    showModalWithWebView: showModalWithWebView,
    showModalWithWebViewAndWaitForResult: showModalWithWebViewAndWaitForResult,
    showOpenPushPrefsPanel: showOpenPushPrefsPanel,
    showSearchOverlay: showSearchOverlay,
    startIndeedApply: startIndeedApply,
    webOnboardingOnComplete: webOnboardingOnComplete
});

// DROID-1733: adding the condition because window is not available during server side rendering
if (typeof window !== 'undefined') {
    // The purpose of the JSMABridgeReceiver is to limit exposure of what we
    //  publish to the global 'window' namespace to a single object/function.
    //
    // The receiveAppResult function *must* be exposed on the global namespace
    //  to let native apps execute callbacks on javascript running inside
    //  their webviews.
    window.JSMABridgeReceiver = {
        receiveAppResult: delegateAppResult,
        receiveWindowEvent: dispatchWindowEvent,
    };
    if (!window.JSMABridgeInstances) {
        window.JSMABridgeInstances = {};
    }
    window.JSMABridgeInstances[bridgeInstanceId] = { receiveAppResult };
    if (!window.JSMABridgeRegistry) {
        window.JSMABridgeRegistry = { executeCallback };
    }
    if (false) {
        // Only used when loaded in native
        const typedWindow = window;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!typedWindow.INDEED_JSMA_BRIDGE) {
            typedWindow.INDEED_JSMA_BRIDGE = JSMABridge;
        }
    }
}

export { Icon, JSMABridge, PageType, SearchType, ToastPosition, ToastStyle, ToastTheme, _INTERNAL_, callPhoneNumber, clearAppBadgeCount, dismissApplyWebView, dismissModalWithWebView, displayErrorAlert, displayPaywall, displaySubscriptionManagement, displayToast, displayWVMPDetails, executeCallback, getNativeAppInfo, indeedApplyDidComplete, isAvailable, isGeolocationGranted, navigateToLogin, navigateToProfileHomePage, openAndroidAppInfo, openEducationPage, openShareDiagnosticsInfoDialog, registerViewJobNavBarSaveButtonClickHandler, sendPageInfo, sendVIPStepInfo, setAppBadgeCount, setConsoleHook, setNavigationTitle, setViewJobNavBarSaveButtonState, shareContent, showActionOverflowMenu, showModalWithWebView, showModalWithWebViewAndWaitForResult, showOpenPushPrefsPanel, showSearchOverlay, startIndeedApply, webOnboardingOnComplete };
